/*
 * Signature — script du thème. JavaScript natif, aucune dépendance.
 * Le contenu reste lisible sans script ; ce fichier ajoute le panier AJAX, les variantes,
 * la galerie, les comptes à rebours à date fixe et quelques interactions discrètes.
 * Seules animations pilotées par le script : tiroirs et accordéons. Les boucles décoratives et les survols
 * sont en CSS. Aucune apparition au défilement, aucun compteur animé.
 */
(() => {
  'use strict';

  /* ---------- Outils ---------- */
  const readJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  };

  /* La sortie du filtre Liquid « t » est échappée en HTML (ex. « qu&#39;un ») : on la décode avant de
     l'écrire en texte. Un textarea décode les entités sans jamais créer d'élément. */
  const decoder = document.createElement('textarea');
  const decodeHTML = (value) => {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    decoder.innerHTML = value;
    return decoder.value;
  };

  const config = readJSON(document.getElementById('ThemeConfig')) || {};
  const routes = config.routes || {};
  const strings = {};
  Object.entries(config.strings || {}).forEach(([key, value]) => {
    strings[key] = decodeHTML(value);
  });
  const html = document.documentElement;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const reducedMotion = () => motionQuery.matches;
  const scrollBehavior = () => (reducedMotion() ? 'auto' : 'smooth');

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  const debounce = (callback, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback(...args), wait);
    };
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const escapeHTML = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const whenIdle = (callback) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 2500 });
    else setTimeout(callback, 300);
  };

  /* Annonce pour les lecteurs d'écran. Quand une fenêtre modale est ouverte (tiroir panier…), le reste de la
     page est inerte : on écrit dans la zone de statut de cette fenêtre si elle en a une. */
  const writeStatus = (region, message) => {
    if (!region || !message) return;
    region.textContent = '';
    setTimeout(() => {
      region.textContent = message;
    }, 80);
  };

  const announce = (message) => {
    if (!message) return;
    const dialogs = [...document.querySelectorAll('dialog[open]')];
    const dialog = dialogs[dialogs.length - 1];
    const region = (dialog && dialog.querySelector('[data-live-region]')) || document.querySelector('body > [data-live-region]');
    writeStatus(region, message);
  };

  const setLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', String(loading));
  };

  /* Désactive un bouton sans perdre le focus clavier : s'il l'avait, le focus passe au bouton opposé. */
  const setDisabledKeepingFocus = (button, disabled, fallback) => {
    if (!button || button.disabled === disabled) return;
    const hadFocus = disabled && document.activeElement === button;
    button.disabled = disabled;
    if (hadFocus && fallback && !fallback.disabled) fallback.focus({ preventScroll: true });
  };

  /* Date de fin d'offre : toujours avec un décalage horaire explicite (le Liquid l'ajoute), sinon le
     navigateur l'interpréterait dans le fuseau du visiteur et la fin varierait d'un visiteur à l'autre. */
  const OFFSET_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;
  const parseEnd = (iso) => (typeof iso === 'string' && OFFSET_PATTERN.test(iso.trim()) ? Date.parse(iso) : NaN);

  const isFuture = (iso) => {
    const time = parseEnd(iso);
    return Number.isFinite(time) && time > Date.now();
  };

  /* Texte d'une offre : nom (personnalisé ou celui du bloc d'achat) + « jusqu'au …, puis … ». */
  const offerLabel = (offer, customText) => {
    if (!offer) return '';
    if (customText) return `${customText} ${decodeHTML(offer.detailHtml || '')}.`;
    return decodeHTML(offer.labelHtml || '');
  };

  /* Forme courte (barre d'annonce sur mobile) : « Offre d'automne jusqu'au 24/10, puis 69 € ». */
  const offerShortLabel = (offer, customText) => {
    if (!offer) return '';
    const name = customText || decodeHTML(offer.textHtml || '');
    const detail = decodeHTML(offer.shortHtml || offer.detailHtml || '');
    return `${name} ${detail}`.trim();
  };

  /* Offre active définie dans le bloc d'achat de la page (date de fin unique). */
  const pageOffer = () => {
    for (const script of document.querySelectorAll('[data-product-json]')) {
      const data = readJSON(script);
      if (data && data.offer && isFuture(data.offer.end)) return data.offer;
    }
    return null;
  };

  /* ---------- Tiroirs (dialog natif) ---------- */
  const unlockIfIdle = () => {
    if (!document.querySelector('dialog[open]')) html.classList.remove('is-locked');
  };

  const openDrawer = (dialog, opener) => {
    if (!dialog) return;
    if (dialog.open) {
      if (dialog.closing) {
        dialog.closing = false;
        dialog.classList.add('is-open');
      }
      return;
    }
    dialog.opener = opener || document.activeElement;
    dialog.showModal();
    html.classList.add('is-locked');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
  };

  const closeDrawer = (dialog) => {
    if (!dialog || !dialog.open || dialog.closing) return;
    dialog.closing = true;
    dialog.classList.remove('is-open');
    const finish = () => {
      if (!dialog.closing) return;
      dialog.closing = false;
      dialog.close();
      const { opener } = dialog;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    };
    if (reducedMotion()) {
      finish();
      return;
    }
    const onEnd = (event) => {
      if (event.target !== dialog) return;
      dialog.removeEventListener('transitionend', onEnd);
      finish();
    };
    dialog.addEventListener('transitionend', onEnd);
    setTimeout(finish, 520);
  };

  const bindDrawer = (dialog) => {
    if (!dialog || dialog.bound) return;
    dialog.bound = true;
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDrawer(dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-drawer-close]')) closeDrawer(dialog);
    });
    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-open');
      dialog.closing = false;
      unlockIfIdle();
      dialog.dispatchEvent(new CustomEvent('drawer:closed'));
    });
  };

  /* ---------- Remise surprise au panier (snippets/cart-surprise.liquid) ----------
     Une seule détection par appel de Cart.render (tiroir et page panier peuvent être remplacés ensemble) :
     - absente puis appliquée : classe is-revealing posée par requestAnimationFrame (animation CSS sobre, coupée si
       le visiteur réduit les animations) et annonce de la remise ;
     - appliquée puis absente (sauf date de fin dépassée : état « expiré », rien n'est dit) : phrase « ne s'applique
       plus » lue avant l'échange (data-surprise-lost), affichée sous les articles jusqu'au rendu suivant, et annoncée.
     Aucun état mémorisé, aucun bouton « Masquer ». Les textes viennent du seul bloc prouvé par Shopify. */
  const Surprise = {
    pending: { text: '', revealed: false },
    read() {
      const block = document.querySelector('[data-surprise="applied"]');
      if (!block) return null;
      return { announce: block.dataset.surpriseAnnounce || '', lost: block.dataset.surpriseLost || '', end: block.dataset.surpriseEnd || '' };
    },
    detect(before) {
      const after = this.read();
      this.pending = { text: '', revealed: false };
      if (after && !before) {
        const blocks = document.querySelectorAll('[data-surprise="applied"]');
        requestAnimationFrame(() => blocks.forEach((block) => block.classList.add('is-revealing')));
        this.pending = { text: after.announce, revealed: true };
      } else if (!after && before && before.lost && !(before.end && !isFuture(before.end))) {
        const lists = document.querySelectorAll('[data-cart-drawer-content] cart-items, [data-cart-page-content] cart-items');
        lists.forEach((list) => {
          const note = document.createElement('p');
          note.className = 'cart-surprise-lost';
          note.textContent = before.lost;
          list.after(note);
        });
        if (lists.length) this.pending = { text: before.lost, revealed: false };
      }
    },
    /* Message de la dernière transition, lu une seule fois (fusionné avec l'annonce de l'action). */
    take() {
      const { pending } = this;
      this.pending = { text: '', revealed: false };
      return pending;
    },
  };

  /* ---------- Panier (API AJAX + Section Rendering) ---------- */
  const Cart = {
    sections() {
      const list = [];
      const drawer = document.querySelector('cart-drawer[data-section-id]');
      if (drawer) list.push({ id: drawer.dataset.sectionId, selector: '[data-cart-drawer-content]' });
      const page = document.querySelector('[data-cart-page][data-section-id]');
      if (page) list.push({ id: page.dataset.sectionId, selector: '[data-cart-page-content]' });
      return list;
    },

    /* Requête POST vers l'API panier. Tout échec réseau (hors ligne, délai dépassé…) donne le message du
       thème, jamais le message technique du navigateur (souvent en anglais). Délai maximal : 15 s. */
    async request(url, body) {
      const isForm = body instanceof FormData;
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (!isForm) headers['Content-Type'] = 'application/json';
      const controller = 'AbortController' in window ? new AbortController() : null;
      const timeout = controller ? setTimeout(() => controller.abort(), 15000) : null;
      let response;
      let data = {};
      try {
        response = await fetch(url, {
          method: 'POST',
          headers,
          body: isForm ? body : JSON.stringify(body),
          signal: controller ? controller.signal : undefined,
        });
        try {
          data = await response.json();
        } catch (error) {
          data = {};
        }
      } catch (error) {
        throw new Error(strings.cartError);
      } finally {
        if (timeout) clearTimeout(timeout);
      }
      if (!response.ok || data.status) {
        /* Réponse d'erreur de Shopify (ex. 422 « stock insuffisant ») : son message est affiché tel quel. */
        const message = response.status >= 400 && response.status < 500 ? data.description || data.message : '';
        throw new Error(message ? decodeHTML(String(message)) : strings.cartError);
      }
      return data;
    },

    /* File d'attente unique : les modifications du panier partent l'une après l'autre, dans l'ordre. */
    queue: Promise.resolve(),

    enqueue(task) {
      const run = this.queue.then(task, task);
      this.queue = run.catch(() => {});
      return run;
    },

    add(formData) {
      formData.append('sections', this.sections().map((section) => section.id).join(','));
      formData.append('sections_url', window.location.pathname);
      return this.request(`${routes.cartAdd}.js`, formData);
    },

    /* Ligne identifiée par sa clé (stable), et non par son rang, qui change quand une ligne est retirée. */
    change(key, quantity) {
      return this.request(`${routes.cartChange}.js`, {
        id: key,
        quantity,
        sections: this.sections().map((section) => section.id),
        sections_url: window.location.pathname,
      });
    },

    /* Remplace le contenu du tiroir et de la page panier. Renvoie true si au moins un rendu a été appliqué. */
    render(sectionsHtml) {
      if (!sectionsHtml) return false;
      const surpriseBefore = Surprise.read();
      let countSource = null;
      this.sections().forEach(({ id, selector }) => {
        const markup = sectionsHtml[id];
        if (!markup) return;
        const source = new DOMParser().parseFromString(markup, 'text/html').querySelector(selector);
        const target = document.querySelector(selector);
        if (!source || !target) return;
        target.innerHTML = source.innerHTML;
        ['data-item-count', 'data-item-count-label'].forEach((name) => {
          if (source.hasAttribute(name)) target.setAttribute(name, source.getAttribute(name));
        });
        if (!countSource) countSource = target;
      });
      if (countSource && countSource.dataset.itemCount !== undefined) {
        this.updateCount(Number(countSource.dataset.itemCount), countSource.dataset.itemCountLabel);
      }
      if (countSource) Surprise.detect(surpriseBefore);
      if (countSource) garderComplement();
      return Boolean(countSource);
    },

    /* Rendu absent de la réponse (sections non renvoyées) ou page restaurée depuis le cache : on redemande
       les sections du panier (API Section Rendering). */
    async refresh() {
      const ids = this.sections().map((section) => section.id);
      if (!ids.length) return false;
      try {
        /* Rendu dans le contexte de la page courante (comme sections_url de add et change) : la section de la page
           panier appartient au modèle cart et n'est pas garantie sur la page d'accueil. */
        const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return false;
        return this.render(await response.json());
      } catch (error) {
        return false;
      }
    },

    subtotalText(scope) {
      const amount = (scope || document).querySelector('.cart-summary__amount');
      return amount ? amount.textContent.trim() : '';
    },

    updateCount(count, label) {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count === 0;
      });
      if (label) {
        document.querySelectorAll('[data-cart-count-label]').forEach((element) => {
          element.textContent = label;
        });
      }
    },
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      bindDrawer(this.dialog);
    }

    open(opener) {
      openDrawer(this.dialog, opener);
    }

    close() {
      closeDrawer(this.dialog);
    }
  }

  /* Lignes du panier : un délai de saisie par ligne, requêtes en file (Cart.enqueue), identification par clé.
     Une saisie vide ou non entière est ignorée (valeur précédente rétablie) : seul un 0 explicite, le bouton
     « moins » ou le lien « Supprimer » retirent une ligne. Entrée valide la quantité sans lancer le paiement. */
  const removingKeys = new Set();

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.timers = new Map();
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-line-quantity]');
        if (!input) return;
        const key = input.dataset.key;
        clearTimeout(this.timers.get(key));
        this.timers.set(
          key,
          setTimeout(() => {
            this.timers.delete(key);
            this.onQuantity(input);
          }, 350)
        );
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        const input = event.target.closest('[data-line-quantity]');
        if (!input) return;
        event.preventDefault();
        clearTimeout(this.timers.get(input.dataset.key));
        this.timers.delete(input.dataset.key);
        this.onQuantity(input);
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-line-remove]');
        if (!remove) return;
        event.preventDefault();
        const { key } = remove.dataset;
        if (!key || removingKeys.has(key)) return;
        removingKeys.add(key);
        Cart.enqueue(() => this.update(key, 0, null)).finally(() => removingKeys.delete(key));
      });
    }

    onQuantity(input) {
      const raw = input.value.trim();
      const previous = input.defaultValue;
      if (!/^\d+$/.test(raw)) {
        input.value = previous;
        return;
      }
      const quantity = parseInt(raw, 10);
      if (String(quantity) === previous) {
        input.value = previous;
        return;
      }
      const { key } = input.dataset;
      if (!key || removingKeys.has(key)) return;
      if (quantity === 0) removingKeys.add(key);
      Cart.enqueue(() => this.update(key, quantity, input)).finally(() => {
        if (quantity === 0) removingKeys.delete(key);
      });
    }

    async update(key, quantity, input) {
      const sectionId = this.dataset.sectionId;
      const container = this.closest('dialog, [data-cart-page]');
      const active = document.activeElement;
      const activeLine = active && active.closest ? active.closest('[data-cart-item]') : null;
      let activeSelector = null;
      if (activeLine && active.matches('[data-quantity-step]')) activeSelector = `[data-quantity-step="${active.dataset.quantityStep}"]`;
      else if (activeLine && active.matches('[data-line-quantity]')) activeSelector = '[data-line-quantity]';

      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        const cart = await Cart.change(key, quantity);
        const rendered = Cart.render(cart.sections) || (await Cart.refresh());
        if (typeof cart.item_count === 'number') Cart.updateCount(cart.item_count);
        const list = document.querySelector(`cart-items[data-section-id="${sectionId}"]`);
        let target = null;
        if (list && activeLine && activeSelector) {
          const row = list.querySelector(`[data-cart-item][data-key="${CSS.escape(activeLine.dataset.key || '')}"]`);
          target = row && row.querySelector(activeSelector);
        }
        if (!target && container) {
          target = container.querySelector('[data-drawer-close], .cart-page__title');
          if (target && target.matches('h1')) target.setAttribute('tabindex', '-1');
        }
        if (target) target.focus({ preventScroll: true });
        /* Un seul message par action : la remise révélée remplace « Panier mis à jour » (elle donne déjà le
           nouveau total) ; la remise perdue le précède. */
        const surprise = Surprise.take();
        const subtotal = rendered ? Cart.subtotalText(container) : '';
        const updated = quantity === 0 && !subtotal ? '' : String(strings.cartUpdated || '').replace('[amount]', subtotal).trim();
        const parts = [quantity === 0 ? strings.cartRemoved : '', surprise.text, surprise.revealed ? '' : updated];
        announce(parts.filter(Boolean).join(' '));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        if (input && input.isConnected) input.value = input.defaultValue;
        const row = this.querySelector(`[data-cart-item][data-key="${CSS.escape(key)}"]`);
        const message = row && row.querySelector('[data-line-error]');
        if (message) {
          message.textContent = error.message;
          message.hidden = false;
        }
        announce(error.message);
      } finally {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input || this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-step]');
        if (!button) return;
        const min = this.input.min === '' ? 0 : Number(this.input.min);
        const max = this.input.max === '' ? Infinity : Number(this.input.max);
        const current = parseInt(this.input.value, 10) || 0;
        const next = clamp(current + Number(button.dataset.quantityStep), min, max);
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.bound) return;
      this.bound = true;
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    async onSubmit(event) {
      const drawer = document.querySelector('cart-drawer');
      /* Panier en page ou tiroir absent : envoi classique vers /cart. */
      if (config.cartType !== 'drawer' || !drawer || !drawer.dialog) return;
      event.preventDefault();
      const button = event.submitter || this.form.querySelector('[type="submit"]');
      if (button && button.classList.contains('is-loading')) return;
      setLoading(button, true);
      this.showError('');
      try {
        const data = await Cart.enqueue(() => Cart.add(new FormData(this.form)));
        if (!Cart.render(data.sections)) await Cart.refresh();
        drawer.open(button);
        /* Un seul message : « Produit ajouté au panier. » suivi, le cas échéant, de l'annonce de la remise. */
        announce([strings.added, Surprise.take().text].filter(Boolean).join(' '));
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: data } }));
      } catch (error) {
        this.showError(error.message);
        announce(error.message);
      } finally {
        setLoading(button, false);
      }
    }
  }

  /* ---------- Bloc d'achat : variantes, prix, stock réel ---------- */
  /* Mêmes règles que snippets/product-stock.liquid : rien sans suivi du stock Shopify (inventory = null) ni
     sans stock positif ; « Plus que N » seulement si la vente en rupture est interdite ; « En stock » en option. */
  const stockState = (variant, threshold, showInStock) => {
    if (!variant || !variant.available) return { visible: false };
    const quantity = variant.inventory;
    if (quantity === null || quantity === undefined || quantity <= 0) return { visible: false };
    if (!variant.continueSelling && threshold > 0 && quantity <= threshold) {
      return {
        visible: true,
        low: true,
        text: quantity === 1 ? strings.stockLowOne : String(strings.stockLowOther || '').replace('[count]', quantity),
        level: Math.round((quantity * 100) / threshold),
      };
    }
    return showInStock ? { visible: true, low: false, text: strings.inStock } : { visible: false };
  };

  /* Texte du prix courant d'un fragment HTML de prix (pour les annonces). */
  const priceText = (markup) => {
    if (!markup) return '';
    const current = new DOMParser().parseFromString(markup, 'text/html').querySelector('.price__current, .offer__total');
    return current ? current.textContent.replace(/\s+/g, ' ').trim() : '';
  };

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.data = readJSON(this.querySelector('[data-product-json]'));
      if (!this.data) return;
      this.bound = true;
      this.picker = this.querySelector('[data-variant-picker]');
      this.status = this.querySelector('[data-product-status]');
      const idInput = this.querySelector('[data-variant-id]');
      this.variant = this.data.variants.find((variant) => idInput && String(variant.id) === idInput.value) || null;
      if (this.picker) {
        this.picker.addEventListener('change', () => this.onOptionChange());
        this.updateAvailability();
      }
      this.offers = this.querySelector('[data-offers]');
      if (this.offers) {
        this.offers.addEventListener('change', (event) => {
          if (event.target.matches('[data-offer-quantity]')) this.onOfferChange(true);
        });
        this.updateOffers(this.variant, false);
        /* Le navigateur peut restaurer un autre lot coché (retour arrière) : on resynchronise la quantité. */
        this.onOfferChange(false);
      }
    }

    /* Lots au-delà du stock réel (stock suivi, vente en rupture interdite) : désactivés, avec la mention « Stock
       insuffisant pour ce lot ». Si le lot choisi le devient, le lot d'une unité (sinon le premier lot possible)
       est repris. Même règle que le Liquid du bloc d'achat. */
    updateOffers(variant, notify = true) {
      if (!this.offers || !variant) return;
      const inputs = [...this.offers.querySelectorAll('[data-offer-quantity]')];
      const limited = variant.available && typeof variant.inventory === 'number' && !variant.continueSelling;
      inputs.forEach((input) => {
        const over = Boolean(limited && Number(input.dataset.offerQuantity) > variant.inventory);
        input.disabled = over;
        const offer = input.closest('.offer');
        if (!offer) return;
        offer.classList.toggle('is-over-stock', over);
        const note = offer.querySelector('[data-offer-stock]');
        if (note) note.hidden = !over;
      });
      const checked = inputs.find((input) => input.checked);
      if (!checked || !checked.disabled) return;
      const fallback = inputs.find((input) => !input.disabled && input.dataset.offerQuantity === '1') || inputs.find((input) => !input.disabled);
      if (!fallback) return;
      fallback.checked = true;
      if (notify) this.onOfferChange(false);
    }

    selectedOffer() {
      const checked = this.offers ? this.offers.querySelector('[data-offer-quantity]:checked') : null;
      if (!checked) return { index: 0, quantity: 1 };
      return { index: Number(checked.dataset.offerIndex) || 0, quantity: Number(checked.dataset.offerQuantity) || 1 };
    }

    /* Recopie la quantité du lot choisi dans l'unique champ « quantity » du formulaire (lu par l'ajout au
       panier et par les paiements express) et prévient la barre collante. */
    onOfferChange(fromUser) {
      const offer = this.selectedOffer();
      const form = document.getElementById(this.data.formId);
      const input = form ? form.querySelector('[data-offer-quantity-input]') : null;
      if (input && input.value !== String(offer.quantity)) {
        input.value = String(offer.quantity);
        input.setAttribute('value', String(offer.quantity));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.offer = offer;
      this.syncOfferTotal();
      document.dispatchEvent(
        new CustomEvent('offer:change', { detail: { formId: this.data.formId, index: offer.index, quantity: offer.quantity, fromUser } })
      );
    }

    /* Rappel du total près du bouton (snippet offer-total) : seule la ligne du lot choisi est visible. */
    syncOfferTotal() {
      const index = this.selectedOffer().index;
      this.querySelectorAll('[data-price-container] [data-offer-total]').forEach((line) => {
        line.hidden = Number(line.dataset.offerTotal) !== index;
      });
    }

    selectedOptions() {
      return [...this.picker.querySelectorAll('fieldset')].map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      this.picker.querySelectorAll('fieldset').forEach((fieldset, index) => {
        const label = fieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = options[index] || '';
      });
      this.variant = this.data.variants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
      this.render(this.variant);
      this.updateAvailability();
      document.dispatchEvent(
        new CustomEvent('variant:change', { detail: { formId: this.data.formId, variant: this.variant, data: this.data } })
      );
    }

    updateAvailability() {
      const selected = this.selectedOptions();
      this.picker.querySelectorAll('fieldset').forEach((fieldset, position) => {
        fieldset.querySelectorAll('input').forEach((input) => {
          const available = this.data.variants.some(
            (variant) =>
              variant.available &&
              variant.options[position] === input.value &&
              variant.options.every((value, index) => index === position || value === selected[index])
          );
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', !available);
          const hint = label.querySelector('[data-unavailable-text]');
          if (hint) hint.hidden = available;
        });
      });
    }

    /* Annonce du nouvel état (lecteurs d'écran) : « Sable, 59,00 €, plus que 7 exemplaires en stock » ou « Épuisé ». */
    announceVariant(variant) {
      if (!this.status) return;
      let message = strings.unavailable;
      if (variant) {
        /* Avec des lots, le prix annoncé est le total du lot choisi. */
        const offerMarkup = this.offers && variant.offersHtml ? variant.offersHtml[this.selectedOffer().index] : '';
        const parts = [variant.title, priceText(offerMarkup || variant.priceHtml)];
        if (!variant.available) parts.push(strings.soldOut);
        else {
          const stock = stockState(variant, this.data.stockThreshold, false);
          if (stock.visible) parts.push(stock.text);
        }
        message = parts.filter(Boolean).join(', ');
      }
      writeStatus(this.status, message);
    }

    render(variant) {
      const button = this.querySelector('[data-main-atc]');
      const label = this.querySelector('[data-atc-label]');
      this.announceVariant(variant);
      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = strings.unavailable;
        this.renderStock(null);
        return;
      }
      const idInput = this.querySelector('[data-variant-id]');
      if (idInput && idInput.value !== String(variant.id)) {
        idInput.value = variant.id;
        /* Les paiements express suivent la variante via cet événement (comme le thème Dawn). */
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;

      const price = this.querySelector('[data-price-container]');
      if (price) price.innerHTML = variant.priceHtml;
      this.querySelectorAll('[data-offer-price]').forEach((element, index) => {
        if (variant.offersHtml && variant.offersHtml[index] !== undefined) element.innerHTML = variant.offersHtml[index];
      });
      this.updateOffers(variant);
      this.syncOfferTotal();
      this.renderStock(variant);

      const gallery = this.querySelector('product-gallery');
      if (variant.mediaId && gallery && typeof gallery.goToMedia === 'function') gallery.goToMedia(variant.mediaId);

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }

    renderStock(variant) {
      const stock = this.querySelector('[data-stock]');
      if (!stock) return;
      const state = stockState(variant, this.data.stockThreshold, stock.dataset.showInStock === 'true');
      stock.hidden = !state.visible;
      if (!state.visible) return;
      stock.classList.toggle('is-low', Boolean(state.low));
      const text = stock.querySelector('[data-stock-text]');
      if (text) text.textContent = state.text;
      const bar = stock.querySelector('[data-stock-bar]');
      if (bar) {
        bar.hidden = !state.low;
        if (state.low && bar.firstElementChild) bar.firstElementChild.style.width = `${state.level}%`;
      }
    }
  }

  /* Note moyenne (bloc d'achat, hero) lue dans la section Avis de la page. */
  class ProductRating extends HTMLElement {
    connectedCallback() {
      const source = document.querySelector('[data-reviews-summary][data-count]');
      if (!source || !Number(source.dataset.count)) {
        this.hidden = true;
        return;
      }
      const stars = this.querySelector('[data-rating-stars]');
      const average = this.querySelector('[data-rating-average]');
      const count = this.querySelector('[data-rating-count]');
      const link = this.querySelector('[data-rating-link]');
      if (stars) stars.style.setProperty('--rating', source.dataset.average);
      if (average) average.textContent = source.dataset.averageLabel;
      if (count) count.textContent = source.dataset.countLabel;
      if (link && source.id) link.setAttribute('href', `#${source.id}`);
      if (link && source.dataset.ratingLabel) link.setAttribute('aria-label', source.dataset.ratingLabel);
      this.classList.remove('is-pending');
    }
  }

  /* ---------- Galerie produit ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track || this.bound) return;
      this.bound = true;
      this.slides = [...this.track.children];
      this.index = 0;
      this.thumbs = this.querySelector('.gallery__thumbs');
      this.addEventListener('click', (event) => {
        const go = event.target.closest('[data-gallery-go]');
        if (go) this.goTo(Number(go.dataset.galleryGo));
        else if (event.target.closest('[data-gallery-prev]')) this.goTo(this.index - 1);
        else if (event.target.closest('[data-gallery-next]')) this.goTo(this.index + 1);
      });
      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.sync();
          });
        },
        { passive: true }
      );
      /* Fin d'un défilement déclenché par le script : l'index suit de nouveau le défilement au doigt. */
      const endProgrammatic = () => {
        clearTimeout(this.programmaticTimer);
        this.programmatic = false;
      };
      this.track.addEventListener('scrollend', endProgrammatic, { passive: true });
      /* Au chargement : pas de défilement des miniatures (évite un recalcul de mise en page forcé). */
      this.update(true);
    }

    /* Position de défilement qui aligne une diapositive sur le bord de départ (marge d'accroche comprise : sur
       mobile, le bandeau déborde jusqu'au bord du cadre et garde une marge intérieure). */
    slideLeft(index, padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0) {
      return Math.max(0, this.slides[index].offsetLeft - padding);
    }

    /* Diapositive courante : la plus proche du bord de départ (sur mobile, les diapositives laissent voir la
       suivante, elles ne font pas toute la largeur) ; au bout du défilement, la dernière. */
    sync() {
      if (this.programmatic) return;
      const left = this.track.scrollLeft;
      const max = this.track.scrollWidth - this.track.clientWidth;
      let index = 0;
      if (max > 0 && left >= max - 2) index = this.slides.length - 1;
      else {
        const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
        let best = Infinity;
        this.slides.forEach((slide, position) => {
          const distance = Math.abs(this.slideLeft(position, padding) - left);
          if (distance < best) {
            best = distance;
            index = position;
          }
        });
      }
      index = clamp(index, 0, this.slides.length - 1);
      if (index === this.index) return;
      this.index = index;
      this.update();
      this.pauseMedia();
    }

    goTo(index) {
      if (!this.slides.length) return;
      const target = clamp(index, 0, this.slides.length - 1);
      /* Pendant le défilement lissé, les positions intermédiaires ne changent pas l'index (pas de clignotement). */
      this.programmatic = true;
      clearTimeout(this.programmaticTimer);
      this.programmaticTimer = setTimeout(() => {
        this.programmatic = false;
      }, 900);
      this.track.scrollTo({ left: this.slideLeft(target), behavior: scrollBehavior() });
      if (target !== this.index) {
        this.index = target;
        this.update();
        this.pauseMedia();
      }
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    update(initial = false) {
      const count = this.querySelector('[data-gallery-count]');
      if (count) count.textContent = String(this.index + 1);
      this.querySelectorAll('[data-gallery-go]').forEach((control) => {
        if (Number(control.dataset.galleryGo) === this.index) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      setDisabledKeepingFocus(prev, this.index === 0, next);
      setDisabledKeepingFocus(next, this.index === this.slides.length - 1, prev);

      if (initial || !this.thumbs) return;
      const current = this.thumbs.querySelector('[aria-current="true"]');
      const item = current && current.parentElement;
      if (!item) return;
      if (this.thumbs.scrollHeight > this.thumbs.clientHeight + 1) {
        this.thumbs.scrollTo({ top: item.offsetTop - (this.thumbs.clientHeight - item.offsetHeight) / 2, behavior: scrollBehavior() });
      } else if (this.thumbs.scrollWidth > this.thumbs.clientWidth + 1) {
        this.thumbs.scrollTo({ left: item.offsetLeft - (this.thumbs.clientWidth - item.offsetWidth) / 2, behavior: scrollBehavior() });
      }
    }

    pauseMedia() {
      this.slides.forEach((slide, index) => {
        if (index === this.index) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
        slide.querySelectorAll('iframe').forEach((frame) => {
          try {
            frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            frame.contentWindow.postMessage('{"method":"pause"}', '*');
          } catch (error) {
            /* lecteur externe indisponible */
          }
        });
      });
    }
  }

  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const poster = this.querySelector('[data-deferred-play]');
      if (poster) poster.addEventListener('click', () => this.load());
    }

    load() {
      if (this.loaded) return;
      const template = this.querySelector('template');
      if (!template) return;
      this.loaded = true;
      this.append(template.content.cloneNode(true));
      const poster = this.querySelector('[data-deferred-play]');
      if (poster) poster.remove();
      const media = this.querySelector('video, iframe');
      if (!media) return;
      if (media instanceof HTMLVideoElement) media.play().catch(() => {});
      media.focus();
    }
  }

  /* Visionneuse plein écran partagée : galerie, photos d'avis. */
  class ZoomDialog extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.dialog = this.querySelector('dialog');
      this.image = this.querySelector('[data-zoom-image]');
      if (!this.dialog || !this.image) return;
      this.bound = true;
      this.counter = this.querySelector('[data-zoom-counter]');
      this.caption = this.querySelector('[data-zoom-caption]');
      this.toggle = this.querySelector('[data-zoom-toggle]');
      this.prev = this.querySelector('[data-zoom-prev]');
      this.next = this.querySelector('[data-zoom-next]');
      this.items = [];
      this.index = 0;

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-zoom-group] [data-zoom-src]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      });
      this.querySelector('[data-zoom-close]').addEventListener('click', () => this.dialog.close());
      this.prev.addEventListener('click', () => this.show(this.index - 1));
      this.next.addEventListener('click', () => this.show(this.index + 1));
      /* Agrandissement au clavier : bouton dédié (aria-pressed) et touches + / -. */
      if (this.toggle) this.toggle.addEventListener('click', () => this.setZoom(!this.dialog.classList.contains('is-zoomed')));
      this.image.addEventListener('click', (event) => this.toggleZoom(event));
      /* Déplacement dans l'image agrandie : souris, doigt ou stylet. */
      this.image.addEventListener('pointermove', (event) => {
        if (this.dialog.classList.contains('is-zoomed') && (event.pointerType === 'mouse' || event.buttons > 0)) this.setOrigin(event);
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.matches('[data-zoom-stage]')) this.dialog.close();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
        if (event.key === 'ArrowRight') this.show(this.index + 1);
        if (event.key === '+' || event.key === '=') this.setZoom(true);
        if (event.key === '-' || event.key === '_') this.setZoom(false);
      });
      this.dialog.addEventListener('close', () => {
        this.setZoom(false);
        unlockIfIdle();
        /* Retour sur la vignette de l'image affichée en dernier (ou, à défaut, sur celle qui a ouvert). */
        const current = this.items[this.index];
        const target = current && current.isConnected ? current : this.opener;
        if (target && target.isConnected) target.focus({ preventScroll: true });
      });
    }

    setZoom(zoomed) {
      this.dialog.classList.toggle('is-zoomed', zoomed);
      if (this.toggle) this.toggle.setAttribute('aria-pressed', String(zoomed));
      if (!zoomed) {
        this.image.style.removeProperty('--zx');
        this.image.style.removeProperty('--zy');
      }
    }

    open(trigger) {
      const group = trigger.closest('[data-zoom-group]');
      this.items = [...group.querySelectorAll('[data-zoom-src]')].filter((item) => !item.closest('[hidden]'));
      this.opener = trigger;
      this.show(Math.max(0, this.items.indexOf(trigger)));
      this.dialog.showModal();
      html.classList.add('is-locked');
    }

    show(index) {
      const total = this.items.length;
      if (!total) return;
      this.index = (index + total) % total;
      const item = this.items[this.index];
      this.setZoom(false);
      this.image.src = item.dataset.zoomSrc;
      this.image.alt = item.dataset.zoomAlt || '';
      if (this.caption) this.caption.textContent = item.dataset.zoomAlt || '';
      if (item.dataset.zoomWidth && item.dataset.zoomHeight) {
        this.image.width = Number(item.dataset.zoomWidth);
        this.image.height = Number(item.dataset.zoomHeight);
      } else {
        this.image.removeAttribute('width');
        this.image.removeAttribute('height');
      }
      if (this.counter) this.counter.textContent = total > 1 ? `${this.index + 1} / ${total}` : '';
      this.prev.hidden = total < 2;
      this.next.hidden = total < 2;
    }

    setOrigin(event) {
      const rect = this.image.getBoundingClientRect();
      const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      this.image.style.setProperty('--zx', `${x}%`);
      this.image.style.setProperty('--zy', `${y}%`);
    }

    toggleZoom(event) {
      this.setOrigin(event);
      this.setZoom(!this.dialog.classList.contains('is-zoomed'));
    }
  }

  /* ---------- Accordéons ---------- */
  class AccordionItem extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.accordion__trigger');
      this.panel = this.querySelector('.accordion__panel');
      if (!this.trigger || !this.panel || this.bound) return;
      this.bound = true;
      this.trigger.addEventListener('click', () => this.setOpen(this.trigger.getAttribute('aria-expanded') !== 'true', true));
      this.panel.addEventListener('beforematch', () => this.setOpen(true, false));
    }

    setOpen(open, animate) {
      const { panel } = this;
      this.trigger.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
      if (this.animation) this.animation.cancel();
      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
      if (open) {
        panel.removeAttribute('hidden');
        if (animate && !reducedMotion()) {
          this.animation = panel.animate(
            [
              { height: '0px', opacity: 0 },
              { height: `${panel.scrollHeight}px`, opacity: 1 },
            ],
            { duration: 420, easing }
          );
        }
        return;
      }
      const hide = () => panel.setAttribute('hidden', 'until-found');
      if (!animate || reducedMotion()) {
        hide();
        return;
      }
      this.animation = panel.animate(
        [
          { height: `${panel.scrollHeight}px`, opacity: 1 },
          { height: '0px', opacity: 0 },
        ],
        { duration: 320, easing }
      );
      this.animation.onfinish = hide;
    }
  }

  /* ---------- En-tête et menu mobile ---------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.bound) return;
      this.bound = true;
      bindDrawer(this.dialog);
      this.dialog.addEventListener('drawer:closed', () => {
        document.querySelectorAll(`[aria-controls="${this.dialog.id}"]`).forEach((button) => button.setAttribute('aria-expanded', 'false'));
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) closeDrawer(this.dialog);
      });
      this.onDesktop = () => {
        if (desktopQuery.matches) closeDrawer(this.dialog);
      };
      desktopQuery.addEventListener('change', this.onDesktop);
    }

    disconnectedCallback() {
      if (this.onDesktop) desktopQuery.removeEventListener('change', this.onDesktop);
    }

    open(opener) {
      if (opener) opener.setAttribute('aria-expanded', 'true');
      openDrawer(this.dialog, opener);
    }
  }

  const updateHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const solid = window.scrollY > 8 || Boolean(header.querySelector('[data-header-dropdown][open]'));
    header.classList.toggle('is-solid', solid);
  };

  const initHeader = (root) => {
    root.querySelectorAll('[data-header-dropdown]').forEach((dropdown) => {
      if (dropdown.dataset.bound) return;
      dropdown.dataset.bound = 'true';
      dropdown.addEventListener('toggle', () => {
        if (dropdown.open) {
          document.querySelectorAll('[data-header-dropdown][open]').forEach((other) => {
            if (other !== dropdown) other.open = false;
          });
        }
        updateHeader();
      });
    });
    updateHeader();
  };

  /* ---------- Barre d'annonce ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.offerItem = this.querySelector('[data-announcement-offer]');
      this.index = 0;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-announcement-prev]')) {
          this.stop();
          this.show(this.index - 1);
        } else if (event.target.closest('[data-announcement-next]')) {
          this.stop();
          this.show(this.index + 1);
        }
      });
      this.addEventListener('mouseenter', () => {
        this.paused = true;
      });
      this.addEventListener('mouseleave', () => {
        this.paused = false;
      });
      this.addEventListener('focusin', () => {
        this.paused = true;
      });
      this.addEventListener('focusout', () => {
        this.paused = false;
      });
      this.addEventListener('countdown:expired', () => {
        if (this.offerItem) this.offerItem.classList.remove('is-active');
        this.refresh();
      });
      this.addEventListener('shopify:block:select', (event) => {
        this.stop();
        const index = this.items().indexOf(event.target);
        if (index >= 0) this.show(index);
      });
      this.syncOffer();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    items() {
      return [...this.querySelectorAll('[data-announcement-item], [data-announcement-offer].is-active')];
    }

    syncOffer() {
      const offer = pageOffer();
      if (this.offerItem) {
        const timer = this.offerItem.querySelector('countdown-timer');
        if (offer && timer) {
          /* Formulation complète : nom de l'offre, date de fin et prix après l'offre, jamais un minuteur seul. */
          const text = this.offerItem.querySelector('[data-offer-text]');
          const short = this.offerItem.querySelector('[data-offer-short]');
          if (text) {
            if (text.dataset.base === undefined) text.dataset.base = text.textContent.trim();
            const custom = this.offerItem.dataset.custom === 'true' ? text.dataset.base : '';
            text.textContent = offerLabel(offer, custom);
            if (short) short.textContent = offerShortLabel(offer, custom);
          }
          this.offerItem.hidden = false;
          this.offerItem.classList.add('is-active');
          timer.setAttribute('data-end', offer.end);
        } else {
          this.offerItem.classList.remove('is-active');
        }
      }
      this.refresh(Boolean(offer));
    }

    refresh(preferOffer = false) {
      const items = this.items();
      if (!items.length) {
        this.hidden = true;
        clearInterval(this.timer);
        return;
      }
      this.hidden = false;
      this.querySelectorAll('[data-announcement-prev], [data-announcement-next]').forEach((button) => {
        button.hidden = items.length < 2;
      });
      let index = items.findIndex((item) => item.classList.contains('is-current'));
      if (preferOffer && this.offerItem && this.offerItem.classList.contains('is-active')) index = items.indexOf(this.offerItem);
      this.show(Math.max(0, index));
      this.start();
    }

    show(index) {
      const items = this.items();
      if (!items.length) return;
      this.index = (index + items.length) % items.length;
      this.querySelectorAll('.announcement__item').forEach((item) => item.classList.remove('is-current'));
      items[this.index].classList.add('is-current');
    }

    start() {
      clearInterval(this.timer);
      if (this.stopped || this.dataset.autoplay !== 'true' || reducedMotion() || this.items().length < 2) return;
      const delay = (Number(this.dataset.speed) || 5) * 1000;
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.show(this.index + 1);
      }, delay);
    }

    stop() {
      this.stopped = true;
      clearInterval(this.timer);
    }
  }

  /* ---------- Compte à rebours (date de fin fixe, jamais relancé) ---------- */
  const timers = new Set();
  let tickTimeout = null;
  const scheduleTick = () => {
    if (tickTimeout || !timers.size) return;
    tickTimeout = setTimeout(() => {
      tickTimeout = null;
      timers.forEach((timer) => timer.tick());
      scheduleTick();
    }, 1000 - (Date.now() % 1000) + 10);
  };
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) timers.forEach((timer) => timer.tick());
  });

  class CountdownTimer extends HTMLElement {
    static get observedAttributes() {
      return ['data-end'];
    }

    connectedCallback() {
      this.started = true;
      this.start();
    }

    disconnectedCallback() {
      timers.delete(this);
      this.started = false;
    }

    /* À la mise à niveau de l'élément, ce rappel précède connectedCallback : on ne démarre qu'une fois. */
    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.started || oldValue === newValue) return;
      this.start();
    }

    start() {
      const raw = this.dataset.end || '';
      if (!raw) {
        timers.delete(this);
        return;
      }
      /* Sans décalage horaire explicite, la date est refusée (fin différente selon le fuseau du visiteur). */
      this.end = parseEnd(raw);
      this.values = {};
      this.querySelectorAll('[data-unit]').forEach((unit) => {
        this.values[unit.dataset.unit] = { unit, value: unit.querySelector('[data-value]') };
      });
      if (!Number.isFinite(this.end)) {
        this.expire();
        return;
      }
      if (this.tick()) {
        timers.add(this);
        scheduleTick();
      }
    }

    tick() {
      const remaining = this.end - Date.now();
      if (remaining <= 0) {
        this.expire();
        return false;
      }
      const total = Math.floor(remaining / 1000);
      const parts = {
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60,
      };
      Object.entries(parts).forEach(([name, amount]) => {
        const entry = this.values[name];
        if (!entry || !entry.value) return;
        const text = name === 'days' ? String(amount) : String(amount).padStart(2, '0');
        if (entry.value.textContent !== text) entry.value.textContent = text;
      });
      if (this.values.days) this.values.days.unit.hidden = parts.days === 0;
      /* Compte à rebours calme (.countdown--calm) : minutes affichées le dernier jour, secondes la dernière heure. */
      this.classList.toggle('is-last-day', total < 86400);
      this.classList.toggle('is-last-hour', total < 3600);
      return true;
    }

    expire() {
      timers.delete(this);
      const container = this.closest('[data-countdown-container]') || this;
      container.hidden = true;
      container.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
    }
  }

  /* Emplacements alimentés par l'offre du bloc d'achat (section « Offre finale »). */
  const initOfferSlots = (root) => {
    const slots = root.querySelectorAll('[data-page-offer-slot]:not([data-ready])');
    if (!slots.length) return;
    const offer = pageOffer();
    if (!offer) return;
    slots.forEach((slot) => {
      const template = slot.querySelector('template[data-countdown-template]');
      const timer = template && template.content.firstElementChild ? template.content.firstElementChild.cloneNode(true) : null;
      if (!timer) return;
      slot.setAttribute('data-ready', '');
      const text = slot.querySelector('[data-offer-text]');
      if (text) {
        const custom = text.dataset.custom === 'true' ? text.textContent.trim() : '';
        text.textContent = offerLabel(offer, custom);
      }
      timer.setAttribute('data-end', offer.end);
      slot.setAttribute('data-countdown-container', '');
      slot.append(timer);
      slot.hidden = false;
    });
  };

  /* ---------- Barre d'achat collante ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      const info = document.querySelector('product-info');
      const data = info ? readJSON(info.querySelector('[data-product-json]')) : null;
      this.button = this.querySelector('[data-sticky-button]');
      /* Produit du bloc d'achat différent de celui de la barre : on masque plutôt que d'annoncer un prix faux. */
      const sameProduct = data && String(data.productId) === String(this.dataset.productId || data.productId);
      if (!data || !sameProduct || !info.querySelector('[data-main-atc]') || !this.button) {
        this.hidden = true;
        return;
      }
      this.bound = true;
      this.data = data;
      this.button.setAttribute('form', data.formId);
      const idInput = info.querySelector('[data-variant-id]');
      this.variant = data.variants.find((variant) => idInput && String(variant.id) === idInput.value) || null;
      this.offer = { index: 0, quantity: 1 };
      /* Un seul bouton d'achat chaud à l'écran à la fois :
         - mobile : visible dès l'arrivée sur le bloc d'achat (le haut de la galerie ou de la carte atteint le
           milieu de l'écran, dès le chargement de la fiche produit ou après un saut d'ancre), sauf tant que le
           bouton principal est lui-même à l'écran ;
         - ordinateur (990 px et plus) : la pilule n'apparaît qu'une fois le bouton principal passé au-dessus de
           l'écran, pour ne jamais recouvrir le bloc d'achat ;
         - dans les deux cas, elle ne recouvre jamais les choix du visiteur : elle se retire tant que la zone qui va
           de la variante cochée (ou du lot choisi) jusqu'au bouton principal passe dans la bande du bas de l'écran
           qu'elle occupe ;
         - masquée aussi tant qu'un autre bouton chaud marqué data-hides-sticky (hero, offre finale) est à
           l'écran. Elle reste affichée ailleurs après le bloc d'achat.
         Le bouton est relu à chaque fois : il est remplacé quand l'éditeur de thème recharge le bloc d'achat. */
      const onScreen = (rect, height) => rect.width > 0 && rect.bottom > 0 && rect.top < height;
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const mainButton = document.querySelector(`#${CSS.escape(data.formId)} [data-main-atc]`);
          if (!mainButton) {
            this.toggle(false);
            return;
          }
          const product = mainButton.closest('product-info') || mainButton;
          const box = product.getBoundingClientRect();
          const button = mainButton.getBoundingClientRect();
          const height = window.innerHeight;
          const reached = desktopQuery.matches ? button.bottom < 0 : box.top < height * 0.5 && !onScreen(button, height);
          const chosen = [...product.querySelectorAll('[data-variant-picker] input:checked + label, [data-offers] input:checked + label')]
            .map((label) => label.getBoundingClientRect())
            .filter((rect) => rect.height > 0);
          const zoneTop = Math.min(button.top, ...chosen.map((rect) => rect.top));
          const barTop = height - this.offsetHeight - (desktopQuery.matches ? 16 : 0);
          const coversChoices = zoneTop < height && button.bottom > barTop;
          const otherButton = [...document.querySelectorAll('[data-hides-sticky]')].some((element) =>
            onScreen(element.getBoundingClientRect(), height)
          );
          this.toggle(reached && !coversChoices && !otherButton);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
      /* Focus clavier sur un élément déjà à l'écran mais caché sous la barre (scroll-padding n'agit que si la page
         défile) : la page remonte juste assez pour le dégager. */
      this.onFocus = (event) => {
        if (!this.classList.contains('is-visible') || this.contains(event.target) || !event.target.getBoundingClientRect) return;
        const target = event.target.getBoundingClientRect();
        const bar = this.getBoundingClientRect();
        const covered = target.bottom > bar.top && target.top < bar.bottom && target.right > bar.left && target.left < bar.right;
        if (covered) window.scrollBy({ top: target.bottom - bar.top + 16, behavior: scrollBehavior() });
      };
      document.addEventListener('focusin', this.onFocus);
      this.onVariant = (event) => {
        if (!event.detail || event.detail.formId !== data.formId) return;
        this.variant = event.detail.variant;
        this.render();
        this.onScroll();
      };
      this.onOffer = (event) => {
        if (!event.detail || event.detail.formId !== data.formId) return;
        this.offer = { index: event.detail.index, quantity: event.detail.quantity };
        this.renderPrice();
        this.onScroll();
      };
      document.addEventListener('variant:change', this.onVariant);
      document.addEventListener('offer:change', this.onOffer);
      const current = info.offer;
      if (current) this.offer = current;
      this.renderPrice();
      this.renderStock(this.variant);
    }

    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.onFocus) document.removeEventListener('focusin', this.onFocus);
      if (this.onVariant) document.removeEventListener('variant:change', this.onVariant);
      if (this.onOffer) document.removeEventListener('offer:change', this.onOffer);
      this.bound = false;
    }

    toggle(visible) {
      if (visible === this.classList.contains('is-visible')) return;
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
    }

    /* Prix du lot choisi : total, badge « -X % » et prix barré repris du lot (même règle que le bloc d'achat,
       snippet price-reference), et nombre d'unités (le formulaire soumet cette quantité). */
    renderPrice() {
      const price = this.querySelector('[data-sticky-price]');
      if (!price) return;
      const { variant } = this;
      if (!variant) {
        price.innerHTML = '';
        return;
      }
      const offerMarkup = variant.offersHtml ? variant.offersHtml[this.offer.index] : '';
      if (this.offer.quantity > 1 && offerMarkup) {
        const offer = new DOMParser().parseFromString(offerMarkup, 'text/html');
        const total = priceText(offerMarkup);
        const badge = offer.querySelector('.price__badge');
        const reference = offer.querySelector('.offer__reference');
        const units = String(strings.unitsLabel || '').replace('[count]', this.offer.quantity);
        price.innerHTML = `<div class="price${reference ? ' price--sale' : ''}"><p class="price__main"><span class="price__current">${escapeHTML(total)}</span>${badge ? badge.outerHTML : ''}</p>${reference ? `<p class="price__reference">${reference.innerHTML}</p>` : ''}<p class="price__unit">${escapeHTML(units)}</p></div>`;
        return;
      }
      price.innerHTML = variant.priceCompactHtml;
    }

    render() {
      const { variant } = this;
      const label = this.querySelector('[data-sticky-label]');
      this.renderPrice();
      this.button.disabled = !variant || !variant.available;
      if (label) {
        if (!variant) label.textContent = strings.unavailable;
        else label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }
      this.renderStock(variant);
    }

    renderStock(variant) {
      const stock = this.querySelector('[data-sticky-stock]');
      if (!stock) return;
      const state = stockState(variant, this.data.stockThreshold, false);
      stock.hidden = !state.visible;
      const text = stock.querySelector('[data-sticky-stock-text]');
      if (text && state.visible) text.textContent = state.text;
    }
  }

  /* ---------- Avis : tri, filtre par note, pagination ---------- */
  class ReviewsList extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.grid = this.querySelector('[data-reviews-grid]');
      if (!this.grid) return;
      this.bound = true;
      this.root = this.closest('[data-reviews-summary]') || this;
      this.cards = [...this.grid.querySelectorAll('.review-card')];
      this.step = Number(this.dataset.step) || 6;
      this.limit = this.step;
      this.isGrid = this.dataset.layout !== 'carousel';
      this.filter = 'all';
      this.sort = 'recent';
      this.more = this.querySelector('[data-reviews-more]');
      this.empty = this.querySelector('[data-reviews-empty]');
      this.sortSelect = this.querySelector('[data-review-sort]');
      if (this.sortSelect) this.sort = this.sortSelect.value || 'recent';

      this.root.addEventListener('click', (event) => {
        const control = event.target.closest('[data-review-filter]');
        if (control) this.setFilter(control.dataset.reviewFilter, control);
      });
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => {
          this.sort = this.sortSelect.value;
          this.apply(true);
        });
      }
      if (this.more) this.more.addEventListener('click', () => this.showMore());
      this.apply(false);
    }

    rating(card) {
      return Math.round(Number(card.dataset.rating) || 0);
    }

    matches(card) {
      return this.filter === 'all' || String(this.rating(card)) === this.filter;
    }

    sorted() {
      const byDate = (a, b) =>
        (b.dataset.date || '').localeCompare(a.dataset.date || '') || Number(a.dataset.index) - Number(b.dataset.index);
      const cards = [...this.cards];
      if (this.sort === 'rating_desc') return cards.sort((a, b) => this.rating(b) - this.rating(a) || byDate(a, b));
      if (this.sort === 'rating_asc') return cards.sort((a, b) => this.rating(a) - this.rating(b) || byDate(a, b));
      return cards.sort(byDate);
    }

    apply(announceCount) {
      const sorted = this.sorted();
      const current = [...this.grid.children];
      if (sorted.some((card, index) => current[index] !== card)) sorted.forEach((card) => this.grid.append(card));
      const visible = sorted.filter((card) => this.matches(card));
      sorted.forEach((card) => {
        const position = visible.indexOf(card);
        card.hidden = position < 0 || (this.isGrid && position >= this.limit);
        card.removeAttribute('data-review-hidden');
      });
      if (this.empty) this.empty.hidden = visible.length > 0;
      if (this.more) this.more.parentElement.hidden = !this.isGrid || visible.length <= this.limit;
      if (announceCount) {
        const shown = this.isGrid ? Math.min(visible.length, this.limit) : visible.length;
        announce(String(strings.reviewsShown || '').replace('[count]', shown));
      }
      return visible;
    }

    setFilter(value, control) {
      const fromSummary = !this.contains(control);
      this.filter = fromSummary && value === this.filter ? 'all' : value;
      this.limit = this.step;
      this.root.querySelectorAll('[data-review-filter]').forEach((button) => {
        const pressed = button.dataset.reviewFilter === this.filter;
        button.setAttribute('aria-pressed', String(pressed));
        button.classList.toggle('is-active', pressed);
      });
      this.apply(true);
      if (fromSummary) {
        const top = this.getBoundingClientRect().top;
        if (top > window.innerHeight * 0.75 || top < 0) this.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      }
    }

    showMore() {
      const before = this.limit;
      this.limit += this.step;
      const visible = this.apply(true);
      const first = visible[before];
      const article = first && first.querySelector('article');
      if (article) {
        article.setAttribute('tabindex', '-1');
        article.focus({ preventScroll: false });
      }
    }
  }

  /* Carrousels simples (citations, avis en carrousel) */
  const updateSlider = (track, scope) => {
    const prev = scope.querySelector('[data-slider-prev]');
    const next = scope.querySelector('[data-slider-next]');
    if (!prev || !next) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    setDisabledKeepingFocus(prev, track.scrollLeft <= 2, next);
    setDisabledKeepingFocus(next, track.scrollLeft >= max, prev);
  };

  const initSliders = (root) => {
    root.querySelectorAll('.quotes, reviews-list').forEach((scope) => {
      const track = scope.querySelector('[data-slider-track]');
      if (!track || !scope.querySelector('[data-slider-next]') || track.dataset.sliderBound) return;
      track.dataset.sliderBound = 'true';
      track.addEventListener('scroll', debounce(() => updateSlider(track, scope), 60), { passive: true });
      updateSlider(track, scope);
    });
  };

  /* ---------- Avant / après ---------- */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('.compare__range');
      this.frame = this.querySelector('.compare__frame');
      if (!this.range || !this.frame || this.bound) return;
      this.bound = true;
      const labelOf = (selector) => {
        const element = this.querySelector(selector);
        return element ? element.textContent.trim() : '';
      };
      const before = labelOf('.compare__label--before');
      const after = labelOf('.compare__label--after');
      const set = (value) => {
        const position = clamp(value, 0, 100);
        const rounded = Math.round(position);
        this.style.setProperty('--position', `${position}%`);
        this.range.value = rounded;
        /* Valeur lue par les lecteurs d'écran : « Avant 60 %, Après 40 % ». */
        this.range.setAttribute('aria-valuetext', `${before} ${rounded} %, ${after} ${100 - rounded} %`.trim());
      };
      set(Number(this.range.value));
      const fromPointer = (event) => {
        const rect = this.frame.getBoundingClientRect();
        set(((event.clientX - rect.left) / rect.width) * 100);
      };
      let dragging = false;
      this.range.addEventListener('input', () => set(Number(this.range.value)));
      this.frame.addEventListener('pointerdown', (event) => {
        dragging = true;
        this.frame.setPointerCapture(event.pointerId);
        fromPointer(event);
      });
      this.frame.addEventListener('pointermove', (event) => {
        if (dragging) fromPointer(event);
      });
      ['pointerup', 'pointercancel'].forEach((type) =>
        this.frame.addEventListener(type, () => {
          dragging = false;
        })
      );
    }
  }

  /* ---------- Date de livraison estimée ---------- */
  /* Jours fériés en France métropolitaine : dates fixes, lundi de Pâques, Ascension, lundi de Pentecôte. */
  const easterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  const holidayCache = new Map();
  const dayKey = (date) => `${date.getMonth() + 1}-${date.getDate()}`;
  const frenchHolidays = (year) => {
    if (!holidayCache.has(year)) {
      const easter = easterSunday(year);
      const fromEaster = (days) => dayKey(new Date(year, easter.getMonth(), easter.getDate() + days));
      holidayCache.set(year, new Set(['1-1', '5-1', '5-8', '7-14', '8-15', '11-1', '11-11', '12-25', fromEaster(1), fromEaster(39), fromEaster(50)]));
    }
    return holidayCache.get(year);
  };
  const isBusinessDay = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !frenchHolidays(date.getFullYear()).has(dayKey(date));
  };

  const addDays = (from, days, businessOnly) => {
    const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    if (!businessOnly) {
      date.setDate(date.getDate() + days);
      return date;
    }
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (isBusinessDay(date)) added += 1;
    }
    return date;
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min);
      const max = Number(this.dataset.max);
      const target = this.querySelector('[data-delivery-text]');
      if (!target || !Number.isFinite(min) || !Number.isFinite(max) || min < 1 || max < min) return;
      const single = max === min;
      const template = single ? this.dataset.templateSingle : this.dataset.template;
      if (!template) return;
      const business = this.dataset.business === 'true';
      const today = new Date();
      const start = addDays(today, min, business);
      const end = addDays(today, max, business);
      const locale = html.lang || 'fr';
      const full = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
      const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
      const startLabel = !single && sameMonth && locale.startsWith('fr') ? String(start.getDate()) : full.format(start);
      target.innerHTML = template.replace('[start]', escapeHTML(startLabel)).replace('[end]', escapeHTML(full.format(end)));
    }
  }

  /* ---------- Médias ---------- */
  /* Pause demandée par le visiteur : les lectures automatiques (visibilité à l'écran) ne la contredisent plus. */
  const autoPause = (video) => {
    if (video.paused) return;
    video.dataset.autoPause = 'true';
    video.pause();
  };
  const trackUserPause = (video) => {
    video.addEventListener('pause', () => {
      if (video.dataset.autoPause) delete video.dataset.autoPause;
      else video.dataset.userPaused = 'true';
    });
    video.addEventListener('play', () => {
      delete video.dataset.userPaused;
    });
  };

  const initAutoplayVideos = (root) => {
    if (reducedMotion() || !('IntersectionObserver' in window)) return;
    root.querySelectorAll('video[data-autoplay-visible]:not([data-ready])').forEach((video) => {
      video.setAttribute('data-ready', '');
      trackUserPause(video);
      new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!video.dataset.userPaused) video.play().catch(() => {});
          } else autoPause(video);
        },
        { threshold: 0.5 }
      ).observe(video);
    });
  };

  /* Nom de marque du pied de page ajusté à la largeur disponible. */
  /* Le texte reste masqué (visibility) jusqu'au premier ajustement : il apparaît à sa taille finale, sans
     décalage de mise en page. */
  const fitText = (element) => {
    const box = element.parentElement;
    if (!box) return;
    const style = getComputedStyle(box);
    const available = box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const width = element.getBoundingClientRect().width;
    if (width && available > 0) {
      const size = Math.min(parseFloat(style.fontSize) * (available / width), 320);
      box.style.fontSize = `${size.toFixed(2)}px`;
    }
    box.classList.add('is-fitted');
  };

  const initFitText = (root) => {
    root.querySelectorAll('[data-fit-text]:not([data-ready])').forEach((element) => {
      element.setAttribute('data-ready', '');
      const run = () => fitText(element);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
      else run();
      if ('ResizeObserver' in window) {
        let lastWidth = 0;
        new ResizeObserver(([entry]) => {
          const width = Math.round(entry.contentRect.width);
          if (width === lastWidth) return;
          lastWidth = width;
          run();
        }).observe(element.parentElement);
      }
    });
  };

  /* ---------- Coloris en tuiles (sections/variant-tiles.liquid) ----------
     Une tuile choisie (bouton radio) change le produit, le nom et le prix au centre ; rien n'est ajouté au
     panier. Le bouton rond sélectionne cette valeur dans le bloc d'achat de la page (même produit) puis y
     descend ; sans bloc d'achat, le lien mène à la page du produit avec cette variante. Un changement de variante
     dans le bloc d'achat met les tuiles à jour. */
  class VariantTiles extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.items = readJSON(this.querySelector('[data-vtiles-json]'));
      if (!Array.isArray(this.items) || !this.items.length) return;
      this.bound = true;
      const labels = readJSON(this.querySelector('[data-vtiles-strings]')) || {};
      this.chooseLabel = decodeHTML(labels.choose || '');
      this.image = this.querySelector('.vtiles__image');
      this.name = this.querySelector('[data-vtiles-name]');
      this.price = this.querySelector('[data-vtiles-price]');
      this.choose = this.querySelector('[data-vtiles-choose]');
      this.status = this.querySelector('[data-vtiles-status]');
      const checked = this.querySelector('.vtiles__input:checked');
      this.index = checked ? Number(checked.dataset.index) || 0 : 0;
      this.addEventListener('change', (event) => {
        if (event.target.matches('.vtiles__input')) this.select(Number(event.target.dataset.index), true);
      });
      if (this.choose) this.choose.addEventListener('click', (event) => this.onChoose(event));
      this.onVariant = (event) => {
        const variant = event.detail && event.detail.variant;
        const data = event.detail && event.detail.data;
        if (!variant || !data || String(data.productId) !== this.dataset.productId) return;
        const value = variant.options[Number(this.dataset.optionPosition) - 1];
        const index = this.items.findIndex((item) => item.value === value);
        if (index < 0 || index === this.index) return;
        const input = this.querySelector(`.vtiles__input[data-index="${index}"]`);
        if (input) input.checked = true;
        this.select(index, false);
      };
      document.addEventListener('variant:change', this.onVariant);
    }

    disconnectedCallback() {
      if (this.onVariant) document.removeEventListener('variant:change', this.onVariant);
      this.bound = false;
    }

    select(index, fromUser) {
      const item = this.items[index];
      if (!item) return;
      this.index = index;
      if (this.name) this.name.textContent = item.value;
      if (this.price && item.priceHtml) this.price.innerHTML = item.priceHtml;
      if (this.choose) {
        this.choose.href = item.url;
        const label = this.choose.querySelector('[data-vtiles-choose-label]');
        if (label) label.textContent = this.chooseLabel.replace('[value]', item.value);
      }
      if (this.image && item.image) {
        const swap = () => {
          this.image.srcset = item.image.srcset || '';
          this.image.src = item.image.src;
          this.image.alt = item.image.alt || '';
          this.image.classList.remove('is-changing');
        };
        if (reducedMotion()) swap();
        else {
          this.image.classList.add('is-changing');
          setTimeout(swap, 160);
        }
      }
      if (fromUser) writeStatus(this.status, item.value);
    }

    onChoose(event) {
      const item = this.items[this.index];
      if (!item) return;
      const info = [...document.querySelectorAll('product-info')].find((element) => {
        const data = element.data || readJSON(element.querySelector('[data-product-json]'));
        return data && String(data.productId) === this.dataset.productId;
      });
      const picker = info && info.querySelector('[data-variant-picker]');
      const fieldset = picker ? picker.querySelectorAll('fieldset')[Number(this.dataset.optionPosition) - 1] : null;
      const input = fieldset ? [...fieldset.querySelectorAll('input')].find((option) => option.value === item.value) : null;
      if (!input) return;
      event.preventDefault();
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const section = info.closest('#MainProduct') || info;
      section.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      const target = info.querySelector('.product__info');
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }
  }

  /* ---------- API publique du panier (page Layal : assets/layal-commun.js) ----------
     Ajout de plusieurs lignes en une requête (JSON { items: [{ id, quantity }] }), avec le même enchaînement que le
     formulaire produit (ProductForm.onSubmit) : file d'attente, rendu du tiroir et des compteurs, ouverture du
     tiroir, annonce, événement cart:added. Panier en page ou tiroir absent : redirection vers le panier.
     Erreur : rejetée avec le message de Shopify (ex. stock insuffisant) ou le message du thème. */
  window.SignatureCart = {
    async addItems(items, opener) {
      const drawer = document.querySelector('cart-drawer');
      const useDrawer = config.cartType === 'drawer' && drawer && drawer.dialog;
      const data = await Cart.enqueue(() =>
        Cart.request(`${routes.cartAdd}.js`, {
          items,
          sections: useDrawer ? Cart.sections().map((section) => section.id) : [],
          sections_url: window.location.pathname,
        })
      );
      if (!useDrawer) {
        window.location.href = routes.cart;
        return data;
      }
      if (!Cart.render(data.sections)) await Cart.refresh();
      drawer.open(opener);
      announce([strings.added, Surprise.take().text].filter(Boolean).join(' '));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: data } }));
      return data;
    },
  };

  /* ---------- Panier : ajout d'un coloris (snippets/layal-panier-coloris.liquid) ----------
     Un toucher = une unité de la variante choisie (POST /cart/add.js, dans la file d'attente du panier), puis nouveau
     rendu du tiroir et de la page panier, sans rouvrir le tiroir. Aucun montant n'est calculé ici : la réduction
     automatique (Offre Duo ou Trio) et le total viennent de Shopify. Le focus passe au nouvel encart (ou au bouton de
     fermeture / au titre de la page) ; un échec affiche le message de Shopify dans l'encart et rétablit les boutons. */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest && event.target.closest('[data-panier-coloris]');
    if (!button || button.disabled) return;
    const block = button.closest('[data-panier-coloris-bloc]');
    const id = Number(button.dataset.panierColoris);
    if (!block || !id || block.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    const container = block.closest('dialog, [data-cart-page]');
    const error = block.querySelector('[data-panier-coloris-erreur]');
    const buttons = [...block.querySelectorAll('[data-panier-coloris]')].filter((item) => !item.disabled);
    block.setAttribute('aria-busy', 'true');
    buttons.forEach((item) => {
      item.disabled = true;
    });
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    try {
      const data = await Cart.enqueue(() =>
        Cart.request(`${routes.cartAdd}.js`, {
          items: [{ id, quantity: 1 }],
          sections: Cart.sections().map((section) => section.id),
          sections_url: window.location.pathname,
        })
      );
      const rendered = Cart.render(data.sections) || (await Cart.refresh());
      const target =
        (container && container.querySelector('[data-panier-coloris-titre]')) ||
        (container && container.querySelector('[data-drawer-close], .cart-page__title'));
      if (target) {
        if (target.matches('h1')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
      const surprise = Surprise.take();
      const subtotal = rendered ? Cart.subtotalText(container) : '';
      const updated = subtotal ? String(strings.cartUpdated || '').replace('[amount]', subtotal).trim() : '';
      announce([button.dataset.panierColorisAnnonce || strings.added, surprise.text, surprise.revealed ? '' : updated].filter(Boolean).join(' '));
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: data } }));
    } catch (failure) {
      if (error && block.isConnected) {
        error.textContent = failure.message;
        error.hidden = false;
      }
      announce(failure.message);
    } finally {
      /* Encart toujours en place (échec, ou rendu impossible) : il redevient utilisable. */
      if (block.isConnected) {
        block.removeAttribute('aria-busy');
        buttons.forEach((item) => {
          item.disabled = false;
        });
      }
    }
  });

  /* ---------- Initialisation ---------- */
  define('cart-drawer', CartDrawer);
  define('cart-items', CartItems);
  define('quantity-input', QuantityInput);
  define('product-form', ProductForm);
  define('product-info', ProductInfo);
  define('product-rating', ProductRating);
  define('product-gallery', ProductGallery);
  define('deferred-media', DeferredMedia);
  define('zoom-dialog', ZoomDialog);
  define('accordion-item', AccordionItem);
  define('menu-drawer', MenuDrawer);
  define('announcement-bar', AnnouncementBar);
  define('countdown-timer', CountdownTimer);
  define('sticky-atc', StickyAtc);
  define('reviews-list', ReviewsList);
  define('before-after', BeforeAfter);
  define('delivery-estimate', DeliveryEstimate);
  define('variant-tiles', VariantTiles);

  /* ---------- Panier : garde du complément (snippets/layal-complement-garde.liquid) ----------
     Repère rendu par Shopify quand le panier contient plus de compléments à prix réduit que de maintiens : la ligne
     désignée est réduite (/cart/change.js, dans la file du panier), puis le panier est rendu de nouveau. Sans repère
     (cas normal, et toujours sans produit complément réglé) : rien. Au plus 5 corrections par page (pas de boucle). */
  let gardeEnCours = false;
  let gardeEssais = 0;
  function garderComplement() {
    const marque = document.querySelector('[data-complement-garde]');
    if (!marque || gardeEnCours || gardeEssais >= 5) return;
    const key = marque.dataset.cle;
    const quantity = Number(marque.dataset.quantite);
    if (!key || !(quantity >= 0)) return;
    gardeEnCours = true;
    gardeEssais += 1;
    const message = marque.dataset.message || '';
    Cart.enqueue(() => Cart.change(key, quantity))
      .then(async (data) => {
        gardeEnCours = false;
        if (!Cart.render(data.sections)) await Cart.refresh();
        if (message) announce(message);
      })
      .catch(() => {
        gardeEnCours = false;
      });
  }

  const init = (root) => {
    initHeader(root);
    initOfferSlots(root);
    initSliders(root);
    initAutoplayVideos(root);
    initFitText(root);
  };

  init(document);
  garderComplement();

  let scrollTicking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        scrollTicking = false;
        updateHeader();
      });
    },
    { passive: true }
  );

  document.addEventListener('click', (event) => {
    const cartToggle = event.target.closest('[data-cart-toggle]');
    if (cartToggle && !document.querySelector('[data-cart-page]')) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.dialog) {
        event.preventDefault();
        drawer.open(cartToggle);
      }
      return;
    }

    const menuButton = event.target.closest('[data-menu-open]');
    if (menuButton) {
      const dialog = document.getElementById(menuButton.getAttribute('aria-controls'));
      const drawer = dialog && dialog.closest('menu-drawer');
      if (drawer) drawer.open(menuButton);
      return;
    }

    const compareChip = event.target.closest('[data-compare-col]');
    if (compareChip) {
      const table = document.getElementById(compareChip.getAttribute('aria-controls'));
      if (table) table.dataset.activeCol = compareChip.dataset.compareCol;
      compareChip.parentElement.querySelectorAll('[data-compare-col]').forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip === compareChip));
      });
      return;
    }

    const sliderButton = event.target.closest('[data-slider-prev], [data-slider-next]');
    if (sliderButton) {
      const scope = sliderButton.closest('.quotes, reviews-list');
      const track = scope && scope.querySelector('[data-slider-track]');
      if (!track) return;
      const first = [...track.children].find((child) => !child.hidden);
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const distance = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
      track.scrollBy({ left: sliderButton.hasAttribute('data-slider-prev') ? -distance : distance, behavior: scrollBehavior() });
      return;
    }

    document.querySelectorAll('[data-header-dropdown][open]').forEach((dropdown) => {
      if (!dropdown.contains(event.target)) dropdown.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-header-dropdown][open]').forEach((dropdown) => {
      dropdown.open = false;
      const summary = dropdown.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  /* Tri (collection) : envoi dès le choix d'une option à la souris ou au doigt. Au clavier, une flèche sur la
     liste fermée change la valeur sans recharger la page (WCAG 3.2.2) : envoi avec Entrée ou en quittant la liste. */
  const autoSubmitSelect = (event) =>
    event.target instanceof Element ? event.target.closest('select[data-auto-submit]') : null;
  const submitSelect = (select) => {
    if (!select.form) return;
    if (select.dataset.submitted === undefined) {
      const initial = [...select.options].find((option) => option.defaultSelected) || select.options[0];
      select.dataset.submitted = initial ? initial.value : '';
    }
    if (select.value === select.dataset.submitted) return;
    select.dataset.submitted = select.value;
    if (typeof select.form.requestSubmit === 'function') select.form.requestSubmit();
    else select.form.submit();
  };
  document.addEventListener('pointerdown', (event) => {
    const select = autoSubmitSelect(event);
    if (select) select.dataset.keyboard = 'false';
  });
  document.addEventListener('keydown', (event) => {
    const select = autoSubmitSelect(event);
    if (!select) return;
    select.dataset.keyboard = 'true';
    if (event.key === 'Enter') {
      event.preventDefault();
      submitSelect(select);
    }
  });
  document.addEventListener('change', (event) => {
    const select = autoSubmitSelect(event);
    if (select && select.dataset.keyboard !== 'true') submitSelect(select);
  });
  document.addEventListener('focusout', (event) => {
    const select = autoSubmitSelect(event);
    if (select && select.dataset.keyboard === 'true') submitSelect(select);
  });

  /* Rétractation : récapitulatif de la demande sur l'écran de confirmation. Les valeurs saisies sont gardées
     dans le stockage de session du navigateur le temps de l'envoi (rien n'est transmis ailleurs), puis effacées. */
  const WITHDRAWAL_KEY = 'withdrawal-summary';
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || form.id !== 'WithdrawalForm') return;
    const value = (id) => {
      const field = form.querySelector(`#${id}`);
      return field ? field.value.trim() : '';
    };
    try {
      sessionStorage.setItem(
        WITHDRAWAL_KEY,
        JSON.stringify({ name: value('WithdrawalName'), order: value('WithdrawalOrder'), date: value('WithdrawalDate'), product: value('WithdrawalProduct') })
      );
    } catch (error) {
      /* stockage indisponible (navigation privée) : pas de récapitulatif */
    }
  });

  const initWithdrawalReceipt = () => {
    const receipt = document.querySelector('[data-withdrawal-receipt]');
    if (!receipt) return;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(WITHDRAWAL_KEY) || 'null');
      sessionStorage.removeItem(WITHDRAWAL_KEY);
    } catch (error) {
      saved = null;
    }
    const summary = receipt.querySelector('[data-withdrawal-summary]');
    if (saved && summary) {
      let filled = 0;
      summary.querySelectorAll('[data-withdrawal-value]').forEach((cell) => {
        const text = saved[cell.dataset.withdrawalValue] || '';
        cell.textContent = text;
        const term = cell.previousElementSibling;
        cell.hidden = !text;
        if (term) term.hidden = !text;
        if (text) filled += 1;
      });
      summary.hidden = filled === 0;
    }
    receipt.focus({ preventScroll: false });
  };
  initWithdrawalReceipt();

  /* Page restaurée depuis le cache du navigateur (retour arrière après le paiement) : panier à jour. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) Cart.refresh().then(() => Surprise.take());
  });

  /* Formulaire de rétractation : le premier champ reçoit le focus à l'ouverture. */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details.matches || !details.matches('.withdrawal__step') || !details.open) return;
      const field = details.querySelector('input:not([type="hidden"])');
      if (field) field.focus();
    },
    true
  );

  /* ---------- Éditeur de thème ---------- */
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    document.querySelectorAll('announcement-bar').forEach((bar) => bar.syncOffer());
  });
  document.addEventListener('shopify:section:select', (event) => {
    if (event.detail && event.detail.sectionId === 'cart-drawer') {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.open();
    }
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.detail && event.detail.sectionId === 'cart-drawer') {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.close();
    }
  });
  document.addEventListener('shopify:block:select', (event) => {
    const accordion = event.target.closest && event.target.closest('accordion-item');
    if (accordion && typeof accordion.setOpen === 'function') accordion.setOpen(true, false);
  });
})();
